import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Empty, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { api } from "../../helpers/api";
import { DT_FORMATS } from "../../helpers/constants";
import type { User } from "../../api-models";

// Mirrors bomHistoryEntry (bom_history.go). That type is unexported on the server — it is a
// composed view, not a stored model — so it is declared here rather than generated into
// api-models.
interface HistoryEntry {
  at: string;
  actorId: number;
  actor?: User | null;
  action: string;
  summary: string;
  changes: string[];
  docType?: string;
  docId?: number;
  docNo?: string;
}

// Colour carries the shape of the trail at a glance: what changed the approved BOM, what
// merely moved it along, and what stopped it.
function dotColor(action: string): string {
  if (action.includes("REJECTED")) return "red";
  if (action.includes("APPROVED")) return "green";
  if (action.startsWith("VARIATION_")) return "blue";
  return "gray";
}

// The revision and variation trail of one BOM version: who, when, and what changed. It is
// read from a per-version endpoint rather than the audit register, so seeing a project's
// own BOM history needs BOM.Read on that project and not Audit.Read over everything.
export function BOMVersionHistory({ versionId, refreshKey }: { versionId: number; refreshKey?: string | number }) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<HistoryEntry[]>(`/bom-versions/${versionId}/history`)
      .then((res) => {
        if (!cancelled) setEntries(res.data);
      })
      // A history that cannot be loaded should not take the document down with it.
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, refreshKey]);

  if (!entries) return null;

  return (
    <Card size="small" title="History" style={{ marginTop: 16 }}>
      {entries.length === 0 ? (
        <Empty description="Nothing recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Timeline
          items={entries.map((e) => ({
            color: dotColor(e.action),
            children: (
              <div>
                <Typography.Text strong>{e.summary}</Typography.Text>
                {e.docId && e.docNo && (
                  <>
                    {" "}
                    <Link to={`/bom-variations/${e.docId}`}>{e.docNo}</Link>
                  </>
                )}
                <div>
                  <Typography.Text type="secondary">
                    {e.actor?.name || e.actor?.email || (e.actorId ? `User #${e.actorId}` : "System")} ·{" "}
                    {dayjs(e.at).format(DT_FORMATS.dateTime)}
                  </Typography.Text>
                </div>
                {e.changes.length > 0 && (
                  <ul style={{ margin: "4px 0 0", paddingInlineStart: 20 }}>
                    {e.changes.map((c, i) => (
                      <li key={i}>
                        <Typography.Text>{c}</Typography.Text>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ),
          }))}
        />
      )}
    </Card>
  );
}
